import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import useIntl from '@afosto/utils-react/intl/useIntl';
import useForm from '@afosto/utils-react/validation/useForm';
import Box from '@material-ui/core/Box';
import FormControl from '@material-ui/core/FormControl';
import InputLabel from '@material-ui/core/InputLabel';
import Address from '@afosto/app-components-react/Address';
import CurrencyField from '@afosto/app-components-react/CurrencyField';
import DatePicker from '@afosto/app-components-react/DatePicker';
import Form from '@afosto/app-components-react/Form';
import TextField from '@afosto/app-components-react/TextField';
import getFullName from '@afosto/utils/getFullName';
import { addresses as addressesApi } from '@afosto/contacts/api/Addresses';
import { contacts as contactsApi } from '@afosto/contacts/api/Contacts';
import { oauth as oauthApi } from '@afosto/iam/api/Oauth';
import { organisations as organisationsApi } from '@afosto/contacts/api/Organisations';
import translations from './translations';

const InvoicePreview = ({ invoice }) => {
  const intl = useIntl();
  const [contact, setContact] = useState(null);
  const [client, setClient] = useState(null);
  const [organisation, setOrganisation] = useState(null);
  const [billingAddress, setBillingAddress] = useState(null);

  const contactID = invoice?.customer?.contact?.id;
  const clientID = invoice?.client_id;
  const organisationID = invoice?.customer?.contact?.organisation_id;
  const billingAddressID = invoice?.customer?.contact?.address_id;
  const isPaid = invoice?.is_paid ?? false;
  const isIncludingVat = invoice?.is_including_vat ?? false;
  const form = useForm({
    editInline: true,
    defaultValues: {
      number: invoice?.number ?? 'Concept',
      subtotal: invoice?.subtotal ?? '-',
      total: invoice?.total ?? '',
      paidAt: invoice?.paid_at ?? null,
      createdAt: invoice?.created_at ?? null,
      isPaid,
      isIncludingVat,
    },
  });

  useEffect(() => {
    let isMounted = true;

    const fetchContact = async id => {
      try {
        const response = await contactsApi.getContact(id);
        const { data } = response;

        if (isMounted) {
          setContact(data);
        }
      } catch (err) {
        if (isMounted) {
          setContact(null);
        }
      }
    };

    if (contactID) {
      fetchContact(contactID);
    }

    return () => {
      isMounted = false;
    };
  }, [contactID]);

  useEffect(() => {
    let isMounted = true;

    const fetchClient = async id => {
      try {
        const response = await oauthApi.getClient(id);
        const { data } = response;

        if (isMounted) {
          setClient(data);
        }
      } catch (err) {
        if (isMounted) {
          setClient(null);
        }
      }
    };

    if (clientID) {
      fetchClient(clientID);
    }

    return () => {
      isMounted = false;
    };
  }, [clientID]);

  useEffect(() => {
    let isMounted = true;

    const fetchOrganisation = async id => {
      try {
        const response = await organisationsApi.viewOrganisation(id);
        const { data } = response;

        if (isMounted) {
          setOrganisation(data);
        }
      } catch (err) {
        if (isMounted) {
          setOrganisation(null);
        }
      }
    };

    if (organisationID) {
      fetchOrganisation(organisationID);
    }

    return () => {
      isMounted = false;
    };
  }, [organisationID]);

  useEffect(() => {
    let isMounted = true;

    const fetchAddress = async id => {
      try {
        const response = await addressesApi.viewAddress(id);
        const { data } = response;

        if (isMounted) {
          setBillingAddress(data);
        }
      } catch (err) {
        if (isMounted) {
          setBillingAddress(null);
        }
      }
    };

    if (billingAddressID) {
      fetchAddress(billingAddressID);
    }

    return () => {
      isMounted = false;
    };
  }, [billingAddressID]);

  return (
    <Form form={form}>
      <TextField
        name="number"
        label={intl.formatMessage(translations.fields.number.label)}
        readOnly
      />

      <TextField
        name="customer"
        label={intl.formatMessage(translations.fields.customer.label)}
        value={contact ? getFullName(contact) : '-'}
        readOnly
      />

      <TextField
        name="organisation"
        label={intl.formatMessage(translations.fields.organisation.label)}
        value={organisation?.name ?? '-'}
        readOnly
      />

      <TextField
        name="client"
        label={intl.formatMessage(translations.fields.client.label)}
        value={client?.name ?? '-'}
        readOnly
      />

      <CurrencyField
        name="subtotal"
        label={intl.formatMessage(translations.fields.subtotal.label)}
        readOnly
      />

      <CurrencyField
        name="total"
        label={intl.formatMessage(translations.fields.total.label)}
        readOnly
      />

      <TextField
        name="isIncludingVat"
        label={intl.formatMessage(translations.fields.isIncludingVat.label)}
        value={intl.formatMessage(translations[isIncludingVat ? 'yes' : 'no'])}
        readOnly
      />

      <TextField
        name="isPaid"
        label={intl.formatMessage(translations.fields.isPaid.label)}
        value={intl.formatMessage(translations[isPaid ? 'yes' : 'no'])}
        readOnly
      />

      <DatePicker
        name="paidAt"
        label={intl.formatMessage(translations.fields.paidAt.label)}
        readOnly
      />

      <DatePicker
        name="createdAt"
        label={intl.formatMessage(translations.fields.createdAt.label)}
        readOnly
      />

      <FormControl variant="outlined" margin="normal" disabled fullWidth>
        <InputLabel shrink variant="outlined">
          {intl.formatMessage(translations.fields.billingAddress.label)}
        </InputLabel>
        <Box p={2} pt={3}>
          {billingAddress ? (
            <Address
              company={billingAddress.organisation}
              fullName={getFullName(billingAddress)}
              addressLine1={billingAddress.address_line_1}
              addressLine2={billingAddress.address_line_2}
              postalCode={billingAddress.postal_code}
              city={billingAddress.locality}
              country={billingAddress.country_code}
              administrativeArea={billingAddress.administrative_area}
            />
          ) : (
            '-'
          )}
        </Box>
      </FormControl>
    </Form>
  );
};

InvoicePreview.propTypes = {
  invoice: PropTypes.object.isRequired,
};

export default InvoicePreview;
